import ControlPointIcon from "@mui/icons-material/ControlPoint";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, FormControl, Grid } from "@mui/material";
import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { AutoCompleteMenu } from "@/atoms";
import { identifiersData, inputData, namesData } from "@/data";
import { createClearedField, createDynamicField } from "@/utils";

export default function ContributorsFields({ asyncMenus, fields, isDrawer = false, setFields, setValue, values }) {
    const { t } = useTranslation();

    const selections = useSelector((state) => state.selectionsReducer);

    const {
        // eslint-disable-next-line destructuring-newline/object-property-newline
        labels: { contributors: contributorsLabel, role: roleLabel },
        // eslint-disable-next-line destructuring-newline/object-property-newline
        names: { contributorIds: contributorIdsName, roleId: roleIdName, trackContributors: trackContributorsName },
    } = inputData;

    const { addField: addFieldButton, remove: removeButton } = namesData.buttons;

    const { button: buttonId } = identifiersData.ids;

    useEffect(() => {
        if (fields.length === 0) {
            setFields([
                createDynamicField({
                    contributorIds: [],
                    roleId: "",
                }),
            ]);
        }
    }, [fields.length, setFields]);

    useEffect(
        () => {
            if (fields.length === 1 && !fields[0].roleId && values?.trackContributorRoles?.length > 0)
                setFields(fields);
        },
        [fields, values?.trackContributorRoles, fields, setFields], //eslint-disable-line
    );

    useEffect(() => {
        const validFields = fields
            .filter((field) => Number(field.roleId) && field.contributorIds?.length > 0)
            .map((field) => ({
                contributorIds: field.contributorIds.map(Number),
                roleId: Number(field.roleId),
            }));

        setValue(trackContributorsName, validFields, {
            shouldDirty: true,
            shouldTouch: true,
        });
    }, [fields, setValue, trackContributorsName]);

    const addField = () => {
        setFields([
            ...fields,
            createDynamicField({
                contributorIds: [],
                roleId: "",
            }),
        ]);
    };

    const removeField = (index) => {
        if (fields.length === 1) {
            const updatedFields = fields.map((f, i) =>
                i === index
                    ? createClearedField({
                          contributorIds: [],
                          roleId: "",
                      })
                    : f,
            );
            setFields(updatedFields);
            setValue(trackContributorsName, []);
        } else {
            const updatedFields = fields.filter((_, i) => i !== index);
            setFields(updatedFields);
        }
    };

    const fieldChangeHandler = (index, fieldName, value) => {
        const updatedFields = [...fields];
        updatedFields[index][fieldName] = value;
        if (fieldName === "roleId" || fieldName === "contributorIds") {
            updatedFields[index]._isNew = false;
            updatedFields[index]._cleared = false;
        }
        setFields(updatedFields);
    };

    return (
        <FormControl fullWidth>
            <h3>
                {t("contributors.name")} ({t("labels.optional")})
            </h3>
            <Grid
                spacing={2}
                sx={{
                    mt: 2,
                }}
                container
            >
                {fields.map((fieldItem, index) => {
                    const disabledRoleIds = fields
                        .map((f, i) => (i !== index && f.roleId ? f.roleId : null))
                        .filter((roleId) => roleId !== null && roleId !== "");

                    const originalRole =
                        !fieldItem._isNew && !fieldItem._cleared
                            ? values?.trackContributorRoles?.find((r) => r.role?.value === fieldItem.roleId)?.role || ""
                            : "";

                    const originalContributors =
                        !fieldItem._isNew && !fieldItem._cleared
                            ? values?.trackContributorRoles?.find((r) => r.role?.value === fieldItem.roleId)
                                  ?.contributors || []
                            : [];

                    return (
                        <Grid
                            key={`${fieldItem.uniqueId}`}
                            spacing={1}
                            size={{
                                xs: 12,
                            }}
                            container
                            item
                        >
                            <Grid
                                size={{
                                    md: isDrawer ? 5 : 4,
                                    xs: 5,
                                }}
                                item
                            >
                                <AutoCompleteMenu
                                    asyncMenuData={asyncMenus?.roles}
                                    defaultValue={fieldItem._isNew || fieldItem._cleared ? "" : originalRole}
                                    disabledOptions={disabledRoleIds}
                                    getRenderedOption={(option) => option?.label}
                                    label={roleLabel}
                                    name={`${roleIdName}_${index}`}
                                    optionLabel={(option) => option?.label}
                                    options={selections?.roleIds || []}
                                    isAsyncMenu
                                    isOptional
                                    onChange={(_, option) => fieldChangeHandler(index, roleIdName, option?.value || "")}
                                />
                            </Grid>

                            <Grid
                                size={{
                                    md: isDrawer ? 5 : 4,
                                    xs: 5,
                                }}
                                item
                            >
                                <AutoCompleteMenu
                                    asyncMenuData={asyncMenus?.contributors}
                                    defaultValue={fieldItem._isNew || fieldItem._cleared ? [] : originalContributors}
                                    getRenderedOption={(option) => option?.label}
                                    label={contributorsLabel}
                                    name={`${contributorIdsName}_${index}`}
                                    optionLabel={(option) => option?.label}
                                    options={selections?.contributors || []}
                                    isAsyncMenu
                                    isMultiple
                                    isOptional
                                    onChange={(_, options) => {
                                        const selectedIds = Array.isArray(options)
                                            ? options.map((item) => item.value)
                                            : [];
                                        fieldChangeHandler(index, contributorIdsName, selectedIds);
                                    }}
                                />
                            </Grid>

                            <Grid
                                size={{
                                    xs: isDrawer ? 2 : 4,
                                }}
                                item
                            >
                                <Button
                                    color="error"
                                    id={`${removeButton}_${buttonId}`}
                                    disabled={fields.length === 1 && !fields[0].roleId && !fields[0].contributorIds}
                                    onClick={() => removeField(index)}
                                >
                                    <DeleteIcon />
                                </Button>
                            </Grid>
                        </Grid>
                    );
                })}
                <Box
                    sx={{
                        mt: 2,
                        mx: isDrawer ? "40%" : "32%",
                    }}
                >
                    <Button
                        color="primary"
                        id={`${addFieldButton}_${buttonId}`}
                        size="large"
                        sx={{
                            display: "flex",
                            mx: "auto",
                        }}
                        onClick={addField}
                    >
                        <ControlPointIcon />
                        {t("actions.add")}
                    </Button>
                </Box>
            </Grid>
        </FormControl>
    );
}

ContributorsFields.propTypes = {
    asyncMenus: PropTypes.object,
    error: PropTypes.string,
    fields: PropTypes.array,
    hasError: PropTypes.bool,
    isDrawer: PropTypes.bool,
    setFields: PropTypes.func,
    setValue: PropTypes.func,
    values: PropTypes.object,
};
